import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
const currentRoles = [{
  title: 'Non-Executive Chair',
  org: 'H2Transition Capital Holdings Ltd',
  location: 'Dublin',
  dates: '2025–Present',
  description: 'Focusing on wind/solar energy linked to green hydrogen and derivatives production such as green ammonia/SAF/steel.'
}, {
  title: 'Non-Executive Director',
  org: 'HyOrc Corporation',
  location: 'Houston, TX',
  dates: '2025–Present',
  description: 'Scalable, low-emission energy systems in the hardest-to-abate sectors including green methanol production, and advanced low-carbon gas-fueled engines that power off-grid operations and next-generation locomotives at diesel-fueled economics.'
}, {
  title: 'Non-Executive Director',
  org: 'Kinecx Energy (formerly Firmus Energy)',
  location: 'Antrim, NI',
  dates: '2023–Present',
  description: 'Gas distribution with increasing low-carbon bio-methane. Chair of Sustainability and member of Restructuring Committees.'
}, {
  title: 'Trustee & Board Director',
  org: 'IOM3',
  location: 'London',
  dates: '2024–Present',
  description: 'Also serving as Chair of the Technology Communities Board.'
}, {
  title: 'Board Member',
  org: 'Nottingham University Business School',
  location: 'Nottingham',
  dates: '2023–Present',
  description: 'Serving on the Advisory Board.'
}];
const pastRoles = [{
  title: 'Chairman, Energy Transition Group',
  org: 'IOM3',
  dates: '2020–2024'
}, {
  title: 'Non-Executive Senior Independent Director',
  org: 'Boson Energy SA, Luxembourg',
  dates: '2019–2024'
}, {
  title: 'Chair & Trustee',
  org: 'London Chamber Orchestra Trust',
  dates: '2018–2021'
}, {
  title: 'Non-Executive Director',
  org: 'Vivo Energy, London',
  dates: '2014–2017'
}, {
  title: 'Non-Executive Director',
  org: 'Shell Vivo Lubricants, London',
  dates: '2014–2017'
}, {
  title: 'Non-Executive Director',
  org: 'Equilon Enterprises, Shell Oil Products, Pennzoil-Quaker State (US)',
  dates: '2010–2013'
}, {
  title: 'Trustee & Chair of Finance Committee',
  org: 'River Oaks Chamber Orchestra, Houston',
  dates: '2009–2013'
}, {
  title: 'Sponsor',
  org: 'Oblath International Enrichment Scholarships, Nottingham University',
  dates: '2017–Present'
}];
export function BoardRoles() {
  const [showPastRoles, setShowPastRoles] = useState(false);
  return <section id="board-roles" className="py-24 bg-white" data-id="element-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-25">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} data-id="element-26">
          <div className="mb-16" data-id="element-27">
            <h2 className="font-serif text-3xl md:text-4xl text-navy mb-6" data-id="element-28">
              Current Non-Executive Roles
            </h2>
            <div className="w-16 h-px bg-gold" data-id="element-29"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20" data-id="element-30">
            {currentRoles.map((role, index) => <div key={index} className="bg-offwhite p-8 border-t-4 border-navy hover:border-gold transition-colors duration-300 shadow-sm flex flex-col h-full" data-id="element-31">
                <span className="text-sm font-medium text-gold mb-3 block" data-id="element-32">
                  {role.dates}
                </span>
                <h3 className="font-serif text-xl text-navy font-medium mb-2" data-id="element-33">
                  {role.title}
                </h3>
                <h4 className="text-navy-light font-medium mb-1" data-id="element-34">{role.org}</h4>
                <span className="text-sm text-gray-500 mb-4 block" data-id="element-35">
                  {role.location}
                </span>
                <p className="text-gray-600 text-sm leading-relaxed mt-auto" data-id="element-36">
                  {role.description}
                </p>
              </div>)}
          </div>

          <div className="border-t border-gray-200 pt-16" data-id="element-37">
            <button onClick={() => setShowPastRoles(!showPastRoles)} className="flex items-center justify-between w-full group" data-id="element-38">
              <div data-id="element-39">
                <h2 className="font-serif text-2xl md:text-3xl text-navy mb-2 text-left" data-id="element-40">
                  Past Non-Executive & Trustee Roles
                </h2>
                <p className="text-gray-500 text-left text-sm" data-id="element-41">
                  View historical board appointments
                </p>
              </div>
              <div className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center group-hover:border-gold group-hover:text-gold transition-colors" data-id="element-42">
                {showPastRoles ? <ChevronUp size={20} data-id="element-43" /> : <ChevronDown size={20} data-id="element-44" />}
              </div>
            </button>

            <AnimatePresence data-id="element-45">
              {showPastRoles && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-46">
                  <div className="pt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-47">
                    {pastRoles.map((role, index) => <div key={index} className="flex flex-col border-l-2 border-gray-200 pl-4 py-1" data-id="element-48">
                        <span className="text-sm font-medium text-gray-500 mb-1" data-id="element-49">
                          {role.dates}
                        </span>
                        <h3 className="font-medium text-navy text-lg" data-id="element-50">
                          {role.title}
                        </h3>
                        <p className="text-gray-600 text-sm" data-id="element-51">{role.org}</p>
                      </div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>;
}